import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { platformAPI } from '../../services/api';
import Button from '../../components/common/Button';
import Icon from '../../components/common/Icon';

const CreatePlatform = () => {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset
  } = useForm();

  const onSubmit = async (data) => {
    setSubmitting(true);
    
    try {
      const platformData = {
        platformName: data.platformName,
        active: true
      };

      const response = await platformAPI.createPlatform(platformData);
      
      toast.success('Platform created successfully!');
      reset();
      
      // Navigate back to platforms list after a short delay
      setTimeout(() => {
        navigate('/admin/platforms');
      }, 1500);
      
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to create platform';
      toast.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleBack = () => {
    navigate('/admin/platforms');
  };

  return (
    <div style={{ 
      padding: '24px',
      flex: 1,
      backgroundColor: '#f8fafc',
      display: 'flex',
      flexDirection: 'column',
      width: '100%',
      overflow: 'auto',
      position: 'relative',
      minHeight: 0
    }}>
      <div style={{ maxWidth: '600px', margin: '0 auto', width: '100%' }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '16px',
          marginBottom: '32px'
        }}>
          <button
            onClick={handleBack}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '8px 16px',
              background: '#ffffff',
              border: '1px solid var(--border)',
              borderRadius: '8px',
              color: 'var(--text-sub)',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            <Icon name="arrow-left" size={16} />
            Back
          </button>
          
          <div>
            <h1 style={{
              fontFamily: 'Outfit, sans-serif',
              fontWeight: '700',
              fontSize: '24px',
              color: 'var(--text)',
              margin: 0
            }}>
              Create Platform
            </h1>
            <p style={{
              fontSize: '14px',
              color: 'var(--text-muted)',
              margin: '4px 0 0 0'
            }}>
              Add a new platform for products
            </p>
          </div>
        </div>

        {/* Form Card */}
        <div style={{ 
          background: '#ffffff', 
          borderRadius: '16px', 
          padding: '32px', 
          border: '1px solid #e2e8f0', 
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
          marginBottom: '24px'
        }}>
          <form onSubmit={handleSubmit(onSubmit)} style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
            {/* Platform Name */}
            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '600', color: 'var(--text)' }}>Platform Name *</label>
              <input
                {...register('platformName', { 
                  required: 'Platform name is required',
                  minLength: { 
                    value: 2, 
                    message: 'Platform name must be at least 2 characters' 
                  },
                  maxLength: { 
                    value: 50, 
                    message: 'Platform name cannot exceed 50 characters' 
                  }
                })}
                type="text"
                placeholder="e.g., Amazon, Flipkart, eBay"
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid var(--border)',
                  borderRadius: '8px',
                  backgroundColor: '#ffffff',
                  color: 'var(--text)',
                  fontSize: '14px',
                  borderColor: errors.platformName ? 'var(--red)' : 'var(--border)'
                }}
              />
              {errors.platformName && (
                <p style={{ color: 'var(--red)', fontSize: '12px', marginTop: '4px' }}>{errors.platformName.message}</p>
              )}
            </div>

            {/* Instructions */}
            <div style={{
              padding: '16px',
              background: '#f8fafc',
              borderRadius: '8px',
              border: '1px solid var(--border)'
            }}>
              <div style={{ display: 'flex', gap: '12px' }}>
                <Icon name="info" size={20} color="var(--primary)" />
                <div>
                  <h4 style={{
                    fontFamily: 'Outfit, sans-serif',
                    fontWeight: '600',
                    fontSize: '14px',
                    color: 'var(--text)',
                    margin: '0 0 8px 0'
                  }}>
                    Platform Information
                  </h4>
                  <ul style={{
                    margin: 0,
                    paddingLeft: '16px',
                    fontSize: '13px',
                    color: 'var(--text-sub)',
                    lineHeight: '1.5'
                  }}>
                    <li>Enter the name of the e-commerce platform</li>
                    <li>This platform will be available when creating products</li>
                    <li>Examples: Amazon, Flipkart, eBay, etc.</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div style={{ 
              display: 'flex', 
              gap: '12px',
              paddingTop: '8px'
            }}>
              <Button
                type="button"
                variant="secondary"
                size="md"
                onClick={handleBack}
                style={{ flex: 1 }}
              >
                Cancel
              </Button>
              
              <Button
                type="submit"
                variant="primary"
                size="md"
                loading={submitting}
                style={{ flex: 1 }}
              >
                Create Platform
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CreatePlatform;
